import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import {
  type AdviceCategoryInput,
  AdviceCategoryInputSchema,
} from "@/features/advice/domain/advice.schema";
import { InlineError } from "@/shared/components/data/ErrorState";
import { FormSheet } from "@/shared/components/data/FormSheet";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { getApiErrorField, getApiErrorMessage } from "@/shared/lib/http-errors";
import { toast } from "@/shared/lib/toast";
import {
  useCreateAdviceCategoryMutation,
  useUpdateAdviceCategoryMutation,
} from "@/store/apis/adminApi.enhanced";
import type { AdviceCategory } from "@/store/apis/adminApi.enhanced";

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Si viene, edición; si no, create. */
  category?: AdviceCategory | null;
}

const FORM_ID = "advice-category-form";

/**
 * Form de create/edit de categoría. El `slug` es la primary key — se
 * define solo al crear y no se puede cambiar (edit lo renderiza read-only).
 *
 * Auto-sugerencia de slug: cuando creamos y la usuaria tipea el label,
 * ofrecemos un slug derivado si todavía no modificó manualmente el campo.
 * Si la usuaria tocó el slug, respetamos su valor.
 */
export function AdviceCategoryFormSheet({ open, onOpenChange, category }: Props) {
  const isEdit = Boolean(category);

  const [createCategory, createState] = useCreateAdviceCategoryMutation();
  const [updateCategory, updateState] = useUpdateAdviceCategoryMutation();
  const submitting = createState.isLoading || updateState.isLoading;
  const submitError = createState.error ?? updateState.error;

  const form = useForm<AdviceCategoryInput>({
    resolver: zodResolver(AdviceCategoryInputSchema),
    defaultValues: useMemo(
      () => ({
        slug: category?.slug ?? "",
        label: category?.label ?? "",
        emoji: category?.emoji ?? "",
        position: category?.position,
      }),
      [category],
    ),
    mode: "onBlur",
  });

  useEffect(() => {
    if (open) {
      form.reset({
        slug: category?.slug ?? "",
        label: category?.label ?? "",
        emoji: category?.emoji ?? "",
        position: category?.position,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, category?.slug]);

  // Auto-slug del label mientras creamos — respeta edición manual.
  const slugFieldState = form.getFieldState("slug");
  const labelValue = form.watch("label");
  useEffect(() => {
    if (isEdit) return;
    if (slugFieldState.isDirty) return;
    if (!labelValue) return;
    const generated = toSlug(labelValue);
    form.setValue("slug", generated, { shouldValidate: false, shouldDirty: false });
    // Intencional: no incluir form en deps (es estable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelValue, isEdit, slugFieldState.isDirty]);

  const onSubmit = form.handleSubmit(async (values) => {
    const emoji = values.emoji?.trim() ? values.emoji.trim() : null;
    try {
      if (isEdit && category) {
        await updateCategory({
          slug: category.slug,
          body: { label: values.label, emoji, position: values.position },
        }).unwrap();
        toast.success("Categoría actualizada");
      } else {
        await createCategory({
          body: {
            slug: values.slug,
            label: values.label,
            emoji,
            position: values.position,
          },
        }).unwrap();
        toast.success("Categoría creada");
      }
      onOpenChange(false);
    } catch (err) {
      // Si el back nos dice qué campo causó el conflicto (ej. `slug`
      // duplicado), anclamos el error al input específico. El
      // InlineError general sigue cubriendo errores sin `field`.
      const field = getApiErrorField(err);
      if (field && field in form.getValues()) {
        form.setError(field as keyof AdviceCategoryInput, {
          type: "server",
          message: getApiErrorMessage(err),
        });
      }
    }
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "Editar categoría" : "Nueva categoría"}
      description={
        isEdit
          ? "El slug es inmutable. Cambia el nombre o el emoji si lo necesitas."
          : "Las categorías agrupan los consejos del banco."
      }
      formId={FORM_ID}
      submitLabel={isEdit ? "Guardar cambios" : "Crear categoría"}
      submitting={submitting}
      dirty={form.formState.isDirty}
    >
      <Form {...form}>
        <form id={FORM_ID} onSubmit={onSubmit} className="space-y-5" noValidate>
          <div className="grid grid-cols-[1fr_72px] gap-4">
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    Nombre
                  </FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      placeholder="Ej. Salud mental"
                      className="h-11 text-[13.5px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="emoji"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    Emoji
                  </FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      placeholder="🌿"
                      className="h-11 text-center text-xl"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  Slug
                </FormLabel>
                <FormControl>
                  <Input
                    autoComplete="off"
                    placeholder="salud-mental"
                    className="h-11 font-mono text-[13px] lowercase"
                    disabled={isEdit}
                    {...field}
                  />
                </FormControl>
                <FormDescription className="font-mono text-[11px] text-muted-foreground">
                  {isEdit
                    ? "Inmutable después de crear."
                    : "Solo minúsculas, números y guiones. Se usa en URLs y como ID."}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="position"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  Posición
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    placeholder="Auto"
                    className="h-11 text-[13.5px]"
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                    }
                  />
                </FormControl>
                <FormDescription className="text-[12px]">
                  Determina el orden en la app. Menor número aparece primero.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <InlineError
            error={submitError}
            fallback={isEdit ? "No pudimos guardar los cambios." : "No pudimos crear la categoría."}
          />
        </form>
      </Form>
    </FormSheet>
  );
}

/** Convierte un label libre a un slug kebab-case ASCII. */
function toSlug(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // quita acentos
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}
